import { EmeraldFileUpload } from "@emerald-react/file-upload";
import { EmeraldNotification } from "@emerald-react/notification";
import { EmeraldProgressSpinner } from "@emerald-react/progress-indicator";
import { useUploadOwnerMediaFileMutation } from "@services/media";
import {
  useCreateSectionMediaMutation,
  useDeletePdfSectionMutation,
} from "@services/survey-sections";
import { validatePdfFiles } from "@shared/utils/fileValidation";
import {
  CustomSectionPDF,
  SurveySection,
} from "@survey-types/survey.section.types";
import { useRef, useState } from "react";
import FileList from "./FileList";
import { addSectionPdf } from "@survey/surveySlice";
import { useSurvey } from "@/shared/hooks/useSurveySelectors";

interface CustomSectionProps {
  sectionId: string;
  section: SurveySection;
}

const CustomSection = ({ sectionId, section }: CustomSectionProps) => {
  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Synchronous flag — set before any await so both onChange and onDrop
  // (which both fire on a single file drop) are blocked after the first.
  const isHandlingRef = useRef(false);
  const survey = useSurvey();
  const [uploadOwnerMediaFile] = useUploadOwnerMediaFileMutation();
  const [createSectionMedia] = useCreateSectionMediaMutation();
  const [deletePdfSection] = useDeletePdfSectionMutation();

  const customSectionPdfs = (section.pdfs || []) as CustomSectionPDF[];

  const handleFiles = async (files: File[]) => {
    // Block immediately — must be the first statement, before any await.
    // EmeraldFileUpload fires both onDrop AND onChange for a single drop;
    // a later check would not fire until after the first async yield.
    if (isHandlingRef.current) return;
    isHandlingRef.current = true;

    try {
      if (!sectionId) {
        console.error("Section ID is required for file upload");
        return;
      }

      if (!survey?.id) {
        console.error("Survey ID is required for section media");
        return;
      }

      setValidationError(null);

      if (files.length > 1) {
        setValidationError("Only one file can be uploaded at a time");
        return;
      }

      const validationResult = await validatePdfFiles(files[0]);
      if (!validationResult.isValid) {
        setValidationError(validationResult.errorMessage || "Invalid file");
        return;
      }

      setUploading(true);

      // Await all deletes before uploading — prevents uploading against a
      // section whose PDF record is still being removed on the backend.
      // Each delete is caught individually so a parse/network error on
      // the delete response never prevents the subsequent upload.
      if (customSectionPdfs.length > 0) {
        await Promise.all(
          customSectionPdfs.map((eachFile) =>
            deletePdfSection({
              surveyId: survey.id,
              surveySectionMediaId: eachFile.surveySectionMediaId,
            })
              .unwrap()
              .catch((err) =>
                console.error("PDF delete error (upload will still proceed):", err),
              ),
          ),
        );
      }

      // Upload new file
      const formData = new FormData();
      const eachFile = files[0];
      formData.append("file", eachFile);
      formData.append(
        "media",
        JSON.stringify({
          sectionId: sectionId,
          mediaType: "SURVEYSECTION",
        }),
      );

      const uploadResponse = await uploadOwnerMediaFile(formData).unwrap();

      if (uploadResponse?.cloudfrontUrl) {
        const sectionMediaPayload = {
          surveyId: survey.id,
          surveysectionId: sectionId,
          mediaUrl: uploadResponse.cloudfrontUrl,
          mediaName: eachFile.name,
          mediaSize: eachFile.size,
        };
        const response = await createSectionMedia(sectionMediaPayload).unwrap();
        const { surveySectionMediaId, mediaName, order, mediaUrl, mediaSize } =
          response;
        addSectionPdf({
          surveySectionMediaId,
          mediaName,
          order,
          mediaUrl,
          mediaSize,
          sectionId,
        });
      }
    } catch (error) {
      console.error("Error during PDF upload:", error);
    } finally {
      isHandlingRef.current = false;
      setUploading(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="relative">
        <EmeraldFileUpload
          allowMultiple={false}
          allowDragAndDrop={true}
          acceptedFileTypes={[".pdf"]}
          disabled={uploading}
          inputLabel="Drop files here or Browse"
          onChange={(_, files) => handleFiles(files)}
          onDrop={(_, files) => handleFiles(files)}
        />

        {uploading && (
          <div className="absolute inset-0 bg-white/50 flex justify-center items-center">
            <EmeraldProgressSpinner
              title={""}
              label={"Uploading files..."}
              size="small"
              isWhite={false}
              determinate={false}
            />
          </div>
        )}
      </div>
      {validationError && (
        <EmeraldNotification
          variant="error"
          message={validationError}
          closable
          onClose={() => setValidationError(null)}
          className="w-full!"
        />
      )}
      {customSectionPdfs && customSectionPdfs.map((pdf: CustomSectionPDF) => (
        <FileList
          key={pdf.surveySectionMediaId}
          file={pdf}
          surveyId={survey?.id || ""}
          sectionId={sectionId}
        />
      ))}
    </div>
  );
};

export default CustomSection;
